/*
 * AdminVoxPontBetaPage — global-admin dashboard for VoxPont Beta access.
 *
 * Served at /admin/voxpont-beta (route is gated to the admin custom claim in
 * AppRoutes.jsx). Real-time view of beta usage + invites, per-session detail,
 * invite generation, and revocation.
 *
 * Compat SDK only via services/voxpontBeta. MUI primitives per repo
 * conventions; navy/gold accents on headers only.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useAuth } from "hooks/useAuth";
import ConfirmDialog from "components/common/ConfirmDialog";
import {
  subscribeToAllVoxPontBetaUsage,
  subscribeToAllVoxPontBetaInvites,
  fetchVoxPontBetaSessions,
  generateVoxPontBetaInvite,
  revokeVoxPontBetaInvite,
} from "services/voxpontBeta";

// Configurable per-turn cost estimate — update as Gemini Live pricing clarifies.
const COST_PER_TURN_USD = 0.01;

const BRAND_NAVY = "#0C1E36";
const BRAND_GOLD = "#B88608";

const toDate = (ts) => {
  if (!ts) return null;
  return typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
};

const formatDate = (ts) => {
  const d = toDate(ts);
  return d ? format(d, "MMM d, yyyy") : "—";
};

const formatDateTime = (ts) => {
  const d = toDate(ts);
  return d ? format(d, "MMM d, yyyy h:mm a") : "—";
};

const formatUsd = (amount) => `$${(amount || 0).toFixed(2)}`;

/**
 * Derive a display status for a usage row from its backing invite.
 * @returns {'active' | 'expired' | 'revoked'}
 */
function deriveStatus(usage, invite) {
  if (invite?.revoked) return "revoked";
  const expiresMs = toDate(usage.expiresAt)?.getTime() ?? 0;
  return expiresMs > Date.now() ? "active" : "expired";
}

const STATUS_META = {
  active: { label: "Active", color: "success" },
  expired: { label: "Expired", color: "error" },
  revoked: { label: "Revoked", color: "default" },
};

function SummaryCard({ label, value }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5">{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function AdminVoxPontBetaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  // The /voxpont page that linked here passes its path via navigation state so
  // we can offer a precise "Back" target; fall back to the marketing hub.
  const referrer = location.state?.from || "/voxpont";
  const [signingOut, setSigningOut] = useState(false);
  const [usageRows, setUsageRows] = useState([]);
  const [invites, setInvites] = useState([]);
  const [usageLoaded, setUsageLoaded] = useState(false);
  const [invitesLoaded, setInvitesLoaded] = useState(false);

  const [expandedUid, setExpandedUid] = useState(null);
  const [sessionsByUid, setSessionsByUid] = useState({});
  const [sessionsLoadingUid, setSessionsLoadingUid] = useState(null);

  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [copied, setCopied] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { note: "", durationDays: 90 },
  });

  useEffect(() => {
    const unsubUsage = subscribeToAllVoxPontBetaUsage((rows) => {
      setUsageRows(rows);
      setUsageLoaded(true);
    });
    const unsubInvites = subscribeToAllVoxPontBetaInvites((rows) => {
      setInvites(rows);
      setInvitesLoaded(true);
    });
    return () => {
      unsubUsage();
      unsubInvites();
    };
  }, []);

  const invitesByCode = useMemo(() => {
    const map = {};
    invites.forEach((inv) => {
      map[inv.code ?? inv.id] = inv;
    });
    return map;
  }, [invites]);

  const decoratedRows = useMemo(() => {
    return usageRows
      .map((usage) => {
        const invite = invitesByCode[usage.inviteCode] ?? null;
        const status = deriveStatus(usage, invite);
        const estCost = (usage.totalTurns || 0) * COST_PER_TURN_USD;
        return { usage, invite, status, estCost };
      })
      .sort((a, b) => {
        const aMs = toDate(a.usage.accessGrantedAt)?.getTime() ?? 0;
        const bMs = toDate(b.usage.accessGrantedAt)?.getTime() ?? 0;
        return bMs - aMs;
      });
  }, [usageRows, invitesByCode]);

  const summary = useMemo(() => {
    let active = 0;
    let expired = 0;
    let totalTurns = 0;
    decoratedRows.forEach(({ status, usage }) => {
      if (status === "active") active += 1;
      if (status === "expired") expired += 1;
      totalTurns += usage.totalTurns || 0;
    });
    return {
      total: decoratedRows.length,
      active,
      expired,
      totalTurns,
      totalCost: totalTurns * COST_PER_TURN_USD,
    };
  }, [decoratedRows]);

  const handleToggleRow = async (uid) => {
    if (expandedUid === uid) {
      setExpandedUid(null);
      return;
    }
    setExpandedUid(uid);
    if (!sessionsByUid[uid]) {
      setSessionsLoadingUid(uid);
      try {
        const sessions = await fetchVoxPontBetaSessions(uid);
        setSessionsByUid((prev) => ({ ...prev, [uid]: sessions }));
      } catch (err) {
        console.warn("[AdminVoxPontBeta] failed to load sessions", err);
        setSessionsByUid((prev) => ({ ...prev, [uid]: [] }));
      } finally {
        setSessionsLoadingUid(null);
      }
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revokeTarget?.inviteCode) return;
    setRevoking(true);
    try {
      await revokeVoxPontBetaInvite(revokeTarget.inviteCode);
      setRevokeTarget(null);
    } catch (err) {
      console.warn("[AdminVoxPontBeta] revoke failed", err);
    } finally {
      setRevoking(false);
    }
  };

  const onGenerate = async (data) => {
    setGenerating(true);
    setGenerateError(null);
    setGeneratedUrl(null);
    setCopied(false);
    try {
      const result = await generateVoxPontBetaInvite({
        note: data.note?.trim() || null,
        durationDays: Number(data.durationDays) || 90,
      });
      setGeneratedUrl(result.inviteUrl);
      reset({ note: "", durationDays: 90 });
    } catch (err) {
      setGenerateError(
        err?.message || "Could not generate an invite. Please try again."
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
      navigate("/voxpont-auth");
    } catch (err) {
      console.warn("[AdminVoxPontBeta] sign out failed", err);
      setSigningOut(false);
    }
  };

  if (!usageLoaded || !invitesLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ borderLeft: `4px solid ${BRAND_GOLD}`, pl: 2 }}>
          <Typography variant="h4" sx={{ color: BRAND_NAVY, fontWeight: 700 }}>
            VoxPont Beta
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage beta invites, monitor usage, and track estimated cost.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(referrer)}
          >
            Back
          </Button>
          <LoadingButton
            variant="outlined"
            color="inherit"
            startIcon={<LogoutIcon />}
            loading={signingOut}
            onClick={handleSignOut}
          >
            Sign out
          </LoadingButton>
        </Stack>
      </Box>

      {/* SECTION 1 — Summary cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={2.4}>
          <SummaryCard label="Total users" value={summary.total} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SummaryCard label="Active" value={summary.active} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SummaryCard label="Expired" value={summary.expired} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <SummaryCard label="Total turns" value={summary.totalTurns} />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <SummaryCard label="Est. cost" value={formatUsd(summary.totalCost)} />
        </Grid>
      </Grid>

      {/* SECTION 2 — User table */}
      <Typography variant="h6" sx={{ color: BRAND_NAVY, mb: 1.5 }}>
        Beta users
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Email</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>Granted</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Sessions</TableCell>
              <TableCell align="right">Turns</TableCell>
              <TableCell align="right">Minutes</TableCell>
              <TableCell align="right">Est. cost</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {decoratedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No beta users yet. Generate an invite below to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              decoratedRows.map(({ usage, invite, status, estCost }) => {
                const uid = usage.uid ?? usage.id;
                const isExpanded = expandedUid === uid;
                const statusMeta = STATUS_META[status];
                const sessions = sessionsByUid[uid];
                return (
                  <React.Fragment key={uid}>
                    <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={isExpanded ? "Collapse" : "Expand"}
                          onClick={() => handleToggleRow(uid)}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{usage.email || uid}</TableCell>
                      <TableCell>{invite?.note || "—"}</TableCell>
                      <TableCell>{formatDate(usage.accessGrantedAt)}</TableCell>
                      <TableCell>{formatDate(usage.expiresAt)}</TableCell>
                      <TableCell align="right">{usage.totalSessions || 0}</TableCell>
                      <TableCell align="right">{usage.totalTurns || 0}</TableCell>
                      <TableCell align="right">
                        {Math.round((usage.totalMinutes || 0) * 10) / 10}
                      </TableCell>
                      <TableCell align="right">{formatUsd(estCost)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusMeta.label}
                          color={statusMeta.color}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {status === "active" ? (
                          <LoadingButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setRevokeTarget({
                                uid,
                                email: usage.email,
                                inviteCode: usage.inviteCode,
                              })
                            }
                          >
                            Revoke
                          </LoadingButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0 }} colSpan={11}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, pl: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Sessions
                            </Typography>
                            {sessionsLoadingUid === uid ? (
                              <CircularProgress size={20} />
                            ) : sessions && sessions.length > 0 ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Started</TableCell>
                                    <TableCell>Ended</TableCell>
                                    <TableCell align="right">Minutes</TableCell>
                                    <TableCell align="right">Turns</TableCell>
                                    <TableCell align="right">Fn calls</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {sessions.map((s) => (
                                    <TableRow key={s.id}>
                                      <TableCell>
                                        {formatDateTime(s.startedAt)}
                                      </TableCell>
                                      <TableCell>
                                        {formatDateTime(s.endedAt)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {s.durationMinutes ?? "—"}
                                      </TableCell>
                                      <TableCell align="right">
                                        {s.turns ?? 0}
                                      </TableCell>
                                      <TableCell align="right">
                                        {s.functionCalls ?? 0}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No sessions recorded yet.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* SECTION 3 — Generate invite */}
      <Typography variant="h6" sx={{ color: BRAND_NAVY, mb: 1.5 }}>
        Generate invite
      </Typography>
      <Card variant="outlined">
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onGenerate)} noValidate>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "flex-start" }}
            >
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Note (who is this for?)"
                    placeholder="e.g. John Smith — initial beta"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="durationDays"
                control={control}
                rules={{ min: { value: 1, message: "Must be at least 1 day." } }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Duration (days)"
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    sx={{ minWidth: { sm: 180 } }}
                  />
                )}
              />
              <LoadingButton
                type="submit"
                variant="contained"
                loading={generating}
                sx={{ minWidth: 200, py: 1.75 }}
              >
                Generate invite link
              </LoadingButton>
            </Stack>
          </Box>

          {generateError ? (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setGenerateError(null)}>
              {generateError}
            </Alert>
          ) : null}

          {generatedUrl ? (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              onClose={() => {
                setGeneratedUrl(null);
                setCopied(false);
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Invite created. Send this link to your tester:
              </Typography>
              <TextField
                value={generatedUrl}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? "Copied!" : "Copy link"}>
                        <IconButton size="small" onClick={handleCopy} edge="end">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        title="Revoke beta access?"
        message={
          revokeTarget
            ? `This immediately blocks ${
                revokeTarget.email || "this user"
              } from VoxPont Beta. This cannot be undone from here.`
            : ""
        }
        confirmLabel="Revoke access"
        confirmColor="error"
        loading={revoking}
        onConfirm={handleConfirmRevoke}
      />
    </Container>
  );
}
